import { useEffect, useState, useCallback } from "react";
import apiClient from "../api/apiClient";
import DataTable from "../components/common/DataTable";
import SearchBar from "../components/common/SearchBar";
import PageTitle from "../components/common/PageTitle";
import AddButton from "../components/common/AddButton";
import AddItemModal from "../components/modals/AddItemModal";
import { EditableUserRow } from "../components/table/EditableRow";
import { confirmDelete } from "../utils/confirm";
import {
  toastCreate,
  toastDelete,
  toastUpdate,
  showError,
  showSuccess,
} from "../utils/toast";
import {
  validateRequired,
  validateEmail,
  validatePasswordMatch,
  validatePasswordLength,
} from "../utils/validation";
import { ROLES } from "../config/constants";
import { useCurrentUser } from "../hooks/useCurrentUser";
import {
  STORAGE_KEYS,
  CACHE_DURATION,
  saveToSessionStorage,
  getFromSessionStorage,
  invalidateCache,
} from "../utils/sessionStorage";

export default function ManageUsers() {
  const { role, projectId: currentUserProjectId } = useCurrentUser();
  const isProjectAdmin = role === ROLES.PROJECT_ADMIN;

  // ✅ ROLE OPTIONS
  const roleOptions = isProjectAdmin
    ? [ROLES.USER, ROLES.PROJECT_ADMIN]
    : Object.values(ROLES);

  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");

  const [showAddPopup, setShowAddPopup] = useState(false);
  const [showResetPasswordModal, setShowResetPasswordModal] = useState(false);
  const [resetPasswordUserId, setResetPasswordUserId] = useState(null);
  const [resetPasswordUserName, setResetPasswordUserName] = useState("");

  const [nama, setNama] = useState("");
  const [email, setEmail] = useState("");
  const [hp, setHp] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [projects, setProjects] = useState([]);
  const [selectedProject, setSelectedProject] = useState("");

  const [newPassword, setNewPassword] = useState("");
  const [confirmNewPassword, setConfirmNewPassword] = useState("");
  const [newPasswordMatch, setNewPasswordMatch] = useState(true);

  const [validEmail, setValidEmail] = useState(true);
  const [passwordMatch, setPasswordMatch] = useState(true);

  // ================= FETCH USERS =================
  const fetchUsers = useCallback(async () => {
    const cachedUsers = getFromSessionStorage(
      STORAGE_KEYS.USERS,
      CACHE_DURATION.USERS,
    );

    if (cachedUsers) {
      setUsers(cachedUsers);
      setLoading(false);
      return;
    }

    try {
      const res = await apiClient.get(`/users`);
      setUsers(res.data);
      saveToSessionStorage(STORAGE_KEYS.USERS, res.data);
    } catch {
      showError("Gagal memuat data user");
    } finally {
      setLoading(false);
    }
  }, []);

  // ================= FETCH PROJECT =================
  const fetchProjects = async () => {
    const cached = getFromSessionStorage(
      STORAGE_KEYS.PROJECT_NAMES,
      CACHE_DURATION.PROJECT_NAMES,
    );

    if (cached) {
      setProjects(cached);
      return;
    }

    try {
      const res = await apiClient.get(`/project-names`);
      setProjects(res.data || []);
      saveToSessionStorage(STORAGE_KEYS.PROJECT_NAMES, res.data || []);
    } catch {
      showError("Gagal memuat project");
    }
  };

  useEffect(() => {
    fetchUsers();
    fetchProjects();
  }, [fetchUsers]);

  useEffect(() => {
    if (showAddPopup && isProjectAdmin && currentUserProjectId) {
      setSelectedProject(String(currentUserProjectId));
    }
  }, [showAddPopup]);

  // ================= UPDATE =================
  const updateUser = async (id, updatedData) => {
    const role = updatedData.role || "user";

    if (
      isProjectAdmin &&
      ![ROLES.USER, ROLES.PROJECT_ADMIN].includes(role)
    ) {
      showError("Tidak diizinkan assign role ini");
      return;
    }

    if (role === "project_admin" && !updatedData.project_id) {
      showError("Project Admin harus punya project");
      return;
    }

    try {
      await toastUpdate(
        apiClient.put(`/users/${id}`, updatedData),
        updatedData.nama,
      );

      invalidateCache([STORAGE_KEYS.USERS]);
      fetchUsers();
    } catch (err) {
      showError(err.response?.data?.error || "Gagal update");
    }
  };

  // ================= DELETE =================
  const deleteUser = (id) => {
    const user = users.find((u) => u.id === id);

    confirmDelete(user?.nama, async () => {
      await toastDelete(apiClient.delete(`/users/${id}`), user?.nama);
      invalidateCache([STORAGE_KEYS.USERS]);
      fetchUsers();
    });
  };

  // ================= RESET PASSWORD =================
  const resetPassword = (id) => {
    const user = users.find((u) => u.id === id);
    setResetPasswordUserId(id);
    setResetPasswordUserName(user?.nama || "");
    setShowResetPasswordModal(true);
  };

  const handleResetPassword = async () => {
    if (!validatePasswordMatch(newPassword, confirmNewPassword)) {
      setNewPasswordMatch(false);
      return;
    }

    if (!validatePasswordLength(newPassword)) return;

    try {
      await apiClient.put(`/users/${resetPasswordUserId}/reset-password`, {
        password: newPassword,
      });

      showSuccess("Password berhasil direset");
      setShowResetPasswordModal(false);
    } catch {
      showError("Gagal reset password");
    }
  };

  // ================= ADD USER =================
  const handleAddUser = async () => {
    if (!validateEmail(email)) return;
    if (!validatePasswordMatch(password, confirmPassword)) return;
    if (!validateRequired({ nama, email, hp, password })) return;

  const projectIdValue = selectedProject || null;

    try {
      await toastCreate(
        apiClient.post(`/register`, {
          nama,
          email,
          hp,
          password,
          project_id: projectIdValue,
        }),
        nama,
      );

      setShowAddPopup(false);
      invalidateCache([STORAGE_KEYS.USERS]);
      fetchUsers();
    } catch {
      showError("Gagal tambah user");
    }
  };

  const filteredUsers = (users || []).filter((u) =>
    ((u.nama || "") + (u.email || "") + (u.hp || ""))
      .toLowerCase()
      .includes(search.toLowerCase()),
  );

  const projectOptions = projects;

  if (loading) return <p className="text-center py-10">Loading...</p>;

  return (
    <div className="p-6 pt-20">
      {/* HEADER */}
      <div className="flex justify-between mb-5">
        <PageTitle>Manage Users</PageTitle>
        <AddButton onClick={() => setShowAddPopup(true)} />
      </div>

      {/* SEARCH */}
    <div className="mb-4 space-y-2">
      <SearchBar
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        placeholder="Cari User..."
      />

      <p className="text-gray-500 text-sm">
        {search
          ? `Hasil pencarian: ${filteredUsers.length} user`
          : `Total user: ${users.length}`}
      </p>
    </div>
      

      {/* TABLE */}
      <DataTable
        columns={[
          { field: "index", label: "No", className: "w-[60px] text-center" },

          { field: "nama", label: "Nama" },

          { field: "email", label: "Email" },

          { field: "project", label: "Proyek" },

          { field: "hp", label: "HP" },

          { field: "role", label: "Role", className: "w-[140px]" },

          {
            field: "",
            label: "",
            className: "w-[90px] text-center",
          },
        ]}
        data={filteredUsers}
        renderRow={(u, i) => (
          <EditableUserRow
            key={u.id}
            user={u}
            index={i}
            projects={projects}
            roleOptions={roleOptions}
            onSave={updateUser}
            onDelete={deleteUser}
            onResetPassword={resetPassword}
          />
        )}
      />

      {/* ================= ADD USER MODAL ================= */}
      <AddItemModal
        isOpen={showAddPopup}
        onClose={() => setShowAddPopup(false)}
        onSubmit={handleAddUser}
        title="Tambah User"
        submitLabel="Tambah User"
      >
        <div className="space-y-3">
          <select
            value={selectedProject}
            onChange={(e) => setSelectedProject(e.target.value)}
            className="w-full border px-3 py-2"
          >
            <option value="">Pilih Project</option>
            {projectOptions.map((p) => (
              <option key={p.id} value={p.id}>
                {p.name}
              </option>
            ))}
          </select>

          <input placeholder="Nama" value={nama} onChange={(e) => setNama(e.target.value)} className="w-full border px-3 py-2" />
          <input placeholder="Email" value={email} onChange={(e) => setEmail(e.target.value)} className="w-full border px-3 py-2" />
          <input placeholder="HP" value={hp} onChange={(e) => setHp(e.target.value)} className="w-full border px-3 py-2" />
          <input type="password" placeholder="Password" value={password} onChange={(e) => setPassword(e.target.value)} className="w-full border px-3 py-2" />
          <input type="password" placeholder="Confirm Password" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} className="w-full border px-3 py-2" />
        </div>
      </AddItemModal>

      {/* ================= RESET PASSWORD MODAL ================= */}
      <AddItemModal
        isOpen={showResetPasswordModal}
        onClose={() => setShowResetPasswordModal(false)}
        onSubmit={handleResetPassword}
        title={`Reset Password - ${resetPasswordUserName}`}
        submitLabel="Reset Password"
      >
        <div className="space-y-3">
          <input type="password" placeholder="Password Baru" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} className="w-full border px-3 py-2" />
          <input type="password" placeholder="Ulangi Password" value={confirmNewPassword} onChange={(e) => setConfirmNewPassword(e.target.value)} className="w-full border px-3 py-2" />
          {!newPasswordMatch && <p className="text-red-500">Password tidak cocok</p>}
        </div>
      </AddItemModal>
    </div>
  );
}
